import {
  Row,
  Space,
  Typography,
  theme,
  Avatar,
  Statistic,
  Button,
  Grid,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import React from "react";
import styles from "./GeneralTableHeader.module.css";

interface GeneralTableHeaderProps {
  title: string;
  description?: string;
  extra?: React.ReactNode[];
  children?: React.ReactNode;
  actionButtons?: React.ReactNode[];
  avatarUrl?: string | React.ReactNode;
  imageUrl?: string;
  onBack?: () => void;
  showBackButton?: boolean;
  statisticsCount?: number;
  statisticsLabel?: string;
  statisticsLoading?: boolean;
}

const { Title } = Typography;

const GeneralTableHeader: React.FC<GeneralTableHeaderProps> = ({
  title,
  description,
  extra,
  children,
  actionButtons,
  avatarUrl,
  imageUrl,
  onBack,
  showBackButton,
  statisticsCount,
  statisticsLabel,
  statisticsLoading = false,
}) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isStack = !screens.md;
  const hasStats = statisticsCount !== undefined && Boolean(statisticsLabel);
  const hasActionButtons = Array.isArray(actionButtons)
    ? actionButtons.length > 0
    : Boolean(actionButtons);
  const hasExtra = Array.isArray(extra) ? extra.length > 0 : Boolean(extra);
  const hasActions = hasStats || hasExtra || hasActionButtons;

  return (
    <div
      className={styles.header_container}
      style={{
        padding: `${token.paddingLG}px`,
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        marginBottom: token.marginLG,
        boxShadow:
          "0 1px 2px 0 rgba(0,0,0,.03), 0 1px 6px -1px rgba(0,0,0,.02), 0 2px 4px 0 rgba(0,0,0,.02)",
      }}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: description ? token.marginSM : 0 }}
      >
        <div
          className={styles.title_container}
          style={{ display: "flex", alignItems: "center", gap: token.marginMD }}
        >
          {showBackButton && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{
                padding: `${token.paddingXS}px`,
                height: "auto",
                color: token.colorText,
              }}
              aria-label="Go back"
            />
          )}
          {(avatarUrl || imageUrl) && (
            <Avatar
              size={64}
              src={typeof avatarUrl === "string" ? avatarUrl : imageUrl}
              style={{
                backgroundColor: token.colorPrimary,
                flexShrink: 0,
              }}
            >
              {typeof avatarUrl !== "string" && avatarUrl
                ? avatarUrl
                : undefined}
            </Avatar>
          )}
          <div>
            <Title
              level={4}
              style={{ margin: 0, fontSize: "24px", fontWeight: 600 }}
            >
              {title}
            </Title>
            {description && (
              <Typography.Text
                type="secondary"
                style={{
                  display: "block",
                  marginTop: token.marginXS,
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                {description}
              </Typography.Text>
            )}
          </div>
        </div>
        {!isStack && hasActions && (
          <Space
            size={token.marginMD}
            className={styles.actions_container}
            wrap
          >
            {hasStats && (
              <div style={{ textAlign: "center" }}>
                <Statistic
                  title={statisticsLabel}
                  value={statisticsCount}
                  loading={statisticsLoading}
                  valueStyle={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: token.colorPrimary,
                  }}
                />
              </div>
            )}
            {(extra || actionButtons) && (
              <div
                style={{
                  marginLeft: hasStats ? token.marginLG : 0,
                }}
              >
                {extra ||
                  (Array.isArray(actionButtons) && actionButtons.length > 1 ? (
                    <Space size={token.marginSM} wrap>
                      {actionButtons}
                    </Space>
                  ) : (
                    actionButtons
                  ))}
              </div>
            )}
          </Space>
        )}
      </Row>
      {isStack && hasActions && (
        <div style={{ marginTop: token.marginSM }}>
          <Space
            size={token.marginMD}
            className={styles.actions_container}
            wrap
          >
            {hasStats && (
              <div style={{ textAlign: "center" }}>
                <Statistic
                  title={statisticsLabel}
                  value={statisticsCount}
                  loading={statisticsLoading}
                  valueStyle={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: token.colorPrimary,
                  }}
                />
              </div>
            )}
            {(extra || actionButtons) && (
              <div style={{ marginLeft: 0 }}>
                {extra ||
                  (Array.isArray(actionButtons) && actionButtons.length > 1 ? (
                    <Space size={token.marginSM} wrap>
                      {actionButtons}
                    </Space>
                  ) : (
                    actionButtons
                  ))}
              </div>
            )}
          </Space>
        </div>
      )}
      {children && (
        <div
          style={{
            marginTop: token.marginLG,
            paddingTop: token.paddingMD,
            borderTop: `1px solid ${token.colorBorder}`,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default GeneralTableHeader;
